import { BaseApiService } from './BaseApiService'
import type { CallStatus, TransactionStatus, TransactionType, ReferralStatsResponse } from '@/types/account'

export interface AdminAccount {
  id: string
  email: string
  balance: {
    amount: number
    currency: string
  }
  role: string
  enabled: boolean
  createdAt: string
}

export interface AdminAccountDetail extends AdminAccount {
  verified: boolean
  currencyLocked?: boolean
  affiliateId?: string
  referredCode?: string
  referredCampaignId?: string
  referredAffiliateId?: string
  referralRewardPaidAt?: string
  lastUpdatedAt: string
}

export interface AdminAccountDetailResponse {
  account: AdminAccountDetail
  referralStats: ReferralStatsResponse
}

export interface AdminAccountsResponse {
  accounts: AdminAccount[]
  page: number
  pageSize: number
  totalPages: number
  totalItems: number
}

export interface AdminCall {
  id: number
  externalId: string
  accountId: string
  accountEmail: string
  originNumber: string
  destinationNumber: string
  status: CallStatus
  startTime: string
  endTime: string
  rate: number
  duration: number
  cost: {
    amount: number
    currency: string
  }
  // Agent call fields (optional, present for agent-initiated calls)
  isAgentCall?: boolean
  agentCallId?: string
  voice?: string
  language?: string
  agentCostCents?: number
}

export interface AdminCallsResponse {
  calls: AdminCall[]
  page: number
  pageSize: number
  totalPages: number
  totalItems: number
}

export interface AdminTransaction {
  id: number
  externalId: string
  accountId: string
  accountEmail: string
  type: TransactionType
  status: TransactionStatus
  createdAt: string
  lastUpdatedAt: string
  payment: {
    amount: number
    currency: string
  }
}

export interface AdminTransactionsResponse {
  transactions: AdminTransaction[]
  page: number
  pageSize: number
  totalPages: number
  totalItems: number
}

export interface ProviderBreakdown {
  twilio: { count: number; percentage: number }
  telnyx: { count: number; percentage: number }
  total: number
}

export interface SystemHealth {
  providerSync: {
    lastSync: string | null
    totalRecords: number
    flaggedRecords: number
  }
  scheduler: {
    failedTasks: string[]
    totalTasks: number
    enabledTasks: number
  }
}

export interface RevenueTrendDay {
  date: string
  callRevenue: number
  topupRevenue: number
  totalRevenue: number
}

export interface AdminStats {
  totalAccounts: number
  activeAccounts: number
  totalCalls: number
  totalTransactions: number
  totalCallMinutes: number
  totalCallRevenue: number
  totalTopUpRevenue: number
  recentCalls: AdminCall[]
  recentSignups: AdminAccount[]
  activeCalls: number
  providerBreakdown: ProviderBreakdown
  systemHealth: SystemHealth
  revenueTrend: RevenueTrendDay[]
}

export interface AdminPricingData {
  generatedAt: number | null
  currency: string
  totalRoutes: number
  totalPrefixes: number
  totalCountries: number
  typeCounts: Record<string, number>
  activeVersionId: string | null
}

export interface AdminPricingVersion {
  id: string
  generatedAt: number | null
  currency: string
  totalRoutes: number
  totalPrefixes: number
  isActive: boolean
  createdAt: string
}

export interface AdminPricingVersionsResponse {
  versions: AdminPricingVersion[]
  page: number
  pageSize: number
  totalPages: number
  totalItems: number
}

export interface AdminPricingActivation {
  id: string
  versionId: string
  versionLabel: string
  generatedAt: number | null
  activatedAt: string
  deactivatedAt: string | null
}

export interface AdminPricingActivationsResponse {
  activations: AdminPricingActivation[]
  page: number
  pageSize: number
  totalPages: number
  totalItems: number
}

export interface AdminPricingRate {
  isoCountry: string
  inferredType: string
  destPrefixes: string[]
  friendlyName: string
  provider: string
  origPrefixes: string[]
  costPerMin: number
  pricePerMin: number
  isSurcharge?: boolean
  eligible?: boolean
}

export type MarginValue = number | { multiply: number; add: number }

export interface AdminPricingMarginTier {
  max_cost?: number
  multiply: number
  add?: number
}

export interface AdminPricingConfig {
  id: string
  countries: string[]
  targetCurrency: string
  margins: Record<string, Record<string, MarginValue>>
  marginTiers: AdminPricingMarginTier[]
  minPricePerMin: number
  effectiveAgentPricePerMin?: number | null
  active: boolean
  createdAt: string
  updatedAt: string
}

export interface AdminProvider {
  name: string
  enabled: boolean
}

export interface AdminGenerationRun {
  id: string
  configId: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  pricingVersionId: string | null
  log: string
  errorMessage: string | null
  countriesProcessed: number
  countriesTotal: number
  startedAt: string | null
  completedAt: string | null
  createdAt: string
}

export interface AdminRateLookupRoute {
  provider: string
  origPrefixes: string[]
  costPerMin: number
  pricePerMin: number
  eligible?: boolean
  chosen?: boolean
}

export interface AdminRateLookup {
  isoCountry: string
  inferredType: string
  friendlyName: string
  pricePerMin: number
  pricePerMinCents: number
  currency: string
  routes: AdminRateLookupRoute[]
}

// Provider Call interfaces
export interface AdminProviderCall {
  id: string
  provider: 'twilio' | 'telnyx'
  providerSid: string
  parentSid: string | null
  fromNumber: string | null
  toNumber: string | null
  status: string | null
  startTime: string | null
  endTime: string | null
  durationSeconds: number | null
  price: number | null
  priceUnit: string | null
  callId: string | null
  flag: string | null
  flagResolved: boolean
  flagResolvedAt: string | null
  flagNotes: string | null
  syncedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface AdminProviderCallsResponse {
  providerCalls: AdminProviderCall[]
  page: number
  pageSize: number
  totalPages: number
  totalItems: number
}

export interface AdminProviderCallDetail extends AdminProviderCall {
  matchedCall?: AdminCall
}

export interface AdminProviderCallSyncSummary {
  total: number
  matched: number
  unmatched: number
  flagged: number
  unresolved: number
  flagCounts: Record<string, number>
  lastSync: string | null
}

export type ProfitLossPeriod = '7d' | '30d' | 'this_month' | 'last_month'

export interface ProfitLossStats {
  billedCents: number
  providerCostCents: number
  grossProfitCents: number
  grossMarginPct: number
  topUpCents: number
  stripeFeesCents: number
  fixedCostsCents: number
  numberCostsCents: number
  totalCostsCents: number
  netCashFlowCents: number
  callCount: number
  matchedCalls: number
  estimatedCalls: number
}

export interface ProfitLossTrendDay {
  date: string
  topupRevenue: number
  callRevenue: number
  totalRevenue: number
}

export interface ProfitLossTrendResponse {
  trend: ProfitLossTrendDay[]
  breakevenMonthlyCents: number
}

export interface AdminReferralProgramme {
  id: string
  rewards: ReferralReward[]
  isActive: boolean
  createdAt: string
  deactivatedAt: string | null
}

export interface ReferralReward {
  currency: string
  referrerCreditCents?: number
  refereeCreditCents?: number | null
  flatFeeCents?: number
}

export interface AdminReferralProgrammesResponse {
  referralProgrammes: AdminReferralProgramme[]
  page: number
  pageSize: number
  totalPages: number
  totalItems: number
}

export interface AdminAffiliate {
  id: string
  accountId: string
  accountEmail?: string
  accountCurrency: string
  code: string
  status?: 'active' | null
  sharePercentage: string | number
  revenueShareMonths: number
  rewards: ReferralReward[]
  createdAt: string
  lastUpdatedAt: string
  totalReferredCount?: number
  totalEarnedCents?: number
}

export interface AdminActiveAffiliatesResponse {
  affiliates: AdminAffiliate[]
  page: number
  pageSize: number
  totalPages: number
  totalItems: number
}

export interface AdminReferralCampaign {
  id: string
  code: string
  name: string
  description?: string
  maxUses?: number | null
  usesCount: number
  rewards: ReferralReward[]
  active: boolean
  activeNow: boolean
  activeFrom: string
  activeUntil?: string | null
}

export interface AdminReferralCampaignsResponse {
  campaigns: AdminReferralCampaign[]
  page: number
  pageSize: number
  totalPages: number
  totalItems: number
}

export interface AdminPhoneNumber {
  number: string
  type: string
  provider: string
  capabilities: {
    voice: boolean
    sms: boolean
    mms: boolean
  }
  purchasedAt: string | null
  monthlyCost: string | null
  monthlyCostCurrency: string
  active: boolean
  verifiedCallerId: boolean
  externalId: string | null
  friendlyName: string | null
  syncedAt: string | null
}

export interface AdminPhoneNumbersResponse {
  phoneNumbers: AdminPhoneNumber[]
}

export interface PhoneNumberSyncSummary {
  total: number
  active: number
  inactive: number
  byProvider: Record<string, number>
  voiceCapable: number
  lastSync: string | null
}

export interface AdminEmail {
  uid: number
  subject: string
  from: string[]
  to: string[]
  date: string
  flags: string[]
}

export interface AdminEmailDetail extends AdminEmail {
  cc: string[]
  textBody: string | null
  htmlBody: string | null
  headers: Record<string, string>
}

export interface AdminEmailsResponse {
  emails: AdminEmail[]
  page: number
  pageSize: number
  totalPages: number
  totalItems: number
}

// Agent Voice types
export interface AgentVoice {
  id: string
  expressedGender: 'masculine' | 'feminine'
  language: string
  accent: string
  characteristics: string[]
}

export interface AgentVoicesResponse {
  voices: AgentVoice[]
}

// Agent Call types
export type AgentCallStatus = 'initiating' | 'ringing' | 'connected' | 'ended' | 'failed'

export interface AdminAgentCall {
  id: string
  callId: string
  status: AgentCallStatus
  provider: string
  voice: string
  language: string
  destinationNumber: string
  initialSayText: string | null
  agentCostCents: number
  connectedAt: string | null
  endedAt: string | null
  createdAt: string
  wsPath?: string
}

export interface AdminAgentCallsResponse {
  calls: AdminAgentCall[]
  page: number
  pageSize: number
  totalPages: number
  totalItems: number
}

export interface AgentCallEvent {
  id: string
  eventType: string
  data: Record<string, unknown> | null
  sequence: number
  createdAt: string
}

export interface AdminAgentCallDetail extends AdminAgentCall {
  events: AgentCallEvent[]
}

// Scheduler types
export interface SchedulerTaskRun {
  id: string
  taskKey: string
  status: 'running' | 'completed' | 'failed'
  triggerType: 'manual' | 'scheduled'
  startedAt: string
  completedAt: string | null
  durationMs: number | null
  error: string | null
  log: string | null
}

export interface SchedulerTask {
  key: string
  description: string
  cron: string | null
  enabled: boolean
  lastRun: SchedulerTaskRun | null
  totalRuns: number
}

export interface SchedulerTasksResponse {
  tasks: SchedulerTask[]
}

export interface SchedulerRunsResponse {
  runs: SchedulerTaskRun[]
}

class AdminApiService extends BaseApiService {
  async getPhoneNumbers(): Promise<AdminPhoneNumbersResponse> {
    return this.get<AdminPhoneNumbersResponse>('/admin/phone-numbers', {
      requiresAuth: true,
    })
  }

  async getAccounts(page = 1, pageSize = 10): Promise<AdminAccountsResponse> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
    return this.get<AdminAccountsResponse>(`/admin/accounts?${params.toString()}`, {
      requiresAuth: true,
    })
  }

  async getCalls(page = 1, pageSize = 10): Promise<AdminCallsResponse> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
    return this.get<AdminCallsResponse>(`/admin/calls?${params.toString()}`, {
      requiresAuth: true,
    })
  }

  async getTransactions(page = 1, pageSize = 10): Promise<AdminTransactionsResponse> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
    return this.get<AdminTransactionsResponse>(`/admin/transactions?${params.toString()}`, {
      requiresAuth: true,
    })
  }

  async getStats(): Promise<AdminStats> {
    return this.get<AdminStats>('/admin/stats', {
      requiresAuth: true,
    })
  }

  async getPricing(): Promise<AdminPricingData> {
    return this.get<AdminPricingData>('/admin/pricing', {
      requiresAuth: true,
    })
  }

  async getPricingVersions(page = 1, pageSize = 20): Promise<AdminPricingVersionsResponse> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
    return this.get<AdminPricingVersionsResponse>(`/admin/pricing/versions?${params.toString()}`, {
      requiresAuth: true,
    })
  }

  async activatePricingVersion(id: string): Promise<AdminPricingVersion> {
    return this.post<AdminPricingVersion>(`/admin/pricing/versions/${id}/activate`, {}, {
      requiresAuth: true,
    })
  }

  async getPricingActivations(page = 1, pageSize = 20): Promise<AdminPricingActivationsResponse> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
    return this.get<AdminPricingActivationsResponse>(`/admin/pricing/activations?${params.toString()}`, {
      requiresAuth: true,
    })
  }

  async getPricingVersionRates(id: string, originNumber?: string): Promise<AdminPricingRate[]> {
    const params = originNumber ? `?origin_number=${encodeURIComponent(originNumber)}` : ''
    return this.get<AdminPricingRate[]>(`/admin/pricing/versions/${id}/rates${params}`, {
      requiresAuth: true,
    })
  }

  async deletePricingVersion(id: string): Promise<void> {
    return this.delete<void>(`/admin/pricing/versions/${id}`, {
      requiresAuth: true,
    })
  }

  async lookupRate(number: string, originNumber?: string): Promise<AdminRateLookup> {
    const params = new URLSearchParams({ number })
    if (originNumber) params.set('origin_number', originNumber)
    return this.get<AdminRateLookup>(`/admin/pricing/lookup?${params.toString()}`, {
      requiresAuth: true,
    })
  }

  async getAccount(accountId: string): Promise<AdminAccountDetailResponse> {
    return this.get<AdminAccountDetailResponse>(`/admin/accounts/${accountId}`, {
      requiresAuth: true,
    })
  }

  async getAccountCalls(accountId: string, page = 1, pageSize = 10): Promise<AdminCallsResponse> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
    return this.get<AdminCallsResponse>(`/admin/accounts/${accountId}/calls?${params.toString()}`, {
      requiresAuth: true,
    })
  }

  async getAccountTransactions(accountId: string, page = 1, pageSize = 10): Promise<AdminTransactionsResponse> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
    return this.get<AdminTransactionsResponse>(`/admin/accounts/${accountId}/transactions?${params.toString()}`, {
      requiresAuth: true,
    })
  }

  async getPricingConfig(): Promise<AdminPricingConfig | null> {
    return this.get<AdminPricingConfig | null>('/admin/pricing/config', {
      requiresAuth: true,
    })
  }

  async createPricingConfig(config: Partial<AdminPricingConfig>): Promise<AdminPricingConfig> {
    return this.post<AdminPricingConfig>('/admin/pricing/config', config, {
      requiresAuth: true,
    })
  }

  async deleteAccount(accountId: string): Promise<void> {
    return this.delete<void>(`/admin/accounts/${accountId}`, {
      requiresAuth: true,
    })
  }

  async updatePricingConfig(id: string, config: Partial<AdminPricingConfig>): Promise<AdminPricingConfig> {
    return this.put<AdminPricingConfig>(`/admin/pricing/config/${id}`, config, {
      requiresAuth: true,
    })
  }

  async getProviders(): Promise<{ providers: AdminProvider[] }> {
    return this.get<{ providers: AdminProvider[] }>('/admin/pricing/providers', {
      requiresAuth: true,
    })
  }

  async toggleProvider(provider: string): Promise<{ providers: AdminProvider[] }> {
    return this.post<{ providers: AdminProvider[] }>(`/admin/pricing/providers/${provider}/toggle`, {}, {
      requiresAuth: true,
    })
  }

  async triggerPricingGeneration(autoActivate: boolean): Promise<AdminGenerationRun> {
    return this.post<AdminGenerationRun>('/admin/pricing/generate', { auto_activate: autoActivate }, {
      requiresAuth: true,
    })
  }

  async getGenerationStatus(): Promise<AdminGenerationRun | null> {
    return this.get<AdminGenerationRun | null>('/admin/pricing/generate/status', {
      requiresAuth: true,
    })
  }

  async cancelPricingGeneration(id: string): Promise<AdminGenerationRun> {
    return this.post<AdminGenerationRun>(`/admin/pricing/generate/${id}/cancel`, {}, {
      requiresAuth: true,
    })
  }

  async adjustAccountBalance(accountId: string, amount: number, currency: string): Promise<{ account: AdminAccount }> {
    return this.post<{ account: AdminAccount }>(`/admin/accounts/${accountId}/balance`, { amount, currency }, {
      requiresAuth: true,
    })
  }

async updateAccountRole(accountId: string, role: string): Promise<{ account: AdminAccount }> {
    return this.post<{ account: AdminAccount }>(`/admin/accounts/${accountId}/role`, { role }, {
      requiresAuth: true,
    })
  }

  async blockAccount(accountId: string): Promise<{ account: AdminAccount }> {
    return this.post<{ account: AdminAccount }>(`/admin/accounts/${accountId}/block`, {}, {
      requiresAuth: true,
    })
  }

  async unblockAccount(accountId: string): Promise<{ account: AdminAccount }> {
    return this.post<{ account: AdminAccount }>(`/admin/accounts/${accountId}/unblock`, {}, {
      requiresAuth: true,
    })
  }

  async migrateAccount(accountId: string, targetAccountId: string): Promise<{ account: AdminAccount }> {
    return this.post<{ account: AdminAccount }>(`/admin/accounts/${accountId}/migrate`, { targetAccountId }, {
      requiresAuth: true,
    })
  }

  async regenerateReferralCode(accountId: string): Promise<AdminAccountDetailResponse> {
    return this.post<AdminAccountDetailResponse>(`/admin/accounts/${accountId}/referral-code/regenerate`, {}, {
      requiresAuth: true,
    })
  }

  // Provider Calls API methods
  async getProviderCalls(
    page = 1,
    pageSize = 20,
    filters?: { provider?: string; flag?: string; callId?: string; startDate?: string; endDate?: string }
  ): Promise<AdminProviderCallsResponse> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
    if (filters?.provider) params.append('provider', filters.provider)
    if (filters?.flag) params.append('flag', filters.flag)
    if (filters?.callId) params.append('call_id', filters.callId)
    if (filters?.startDate) params.append('start_date', filters.startDate)
    if (filters?.endDate) params.append('end_date', filters.endDate)
    return this.get<AdminProviderCallsResponse>(`/admin/provider-calls/calls?${params.toString()}`, {
      requiresAuth: true,
    })
  }

  async getProviderCall(id: string): Promise<AdminProviderCallDetail> {
    return this.get<AdminProviderCallDetail>(`/admin/provider-calls/calls/${id}`, {
      requiresAuth: true,
    })
  }

  async getProviderCallSyncSummary(): Promise<AdminProviderCallSyncSummary> {
    return this.get<AdminProviderCallSyncSummary>('/admin/provider-calls/summary', {
      requiresAuth: true,
    })
  }

  async resolveProviderCallFlag(id: string, notes?: string): Promise<{ providerCall: AdminProviderCall }> {
    return this.post<{ providerCall: AdminProviderCall }>(`/admin/provider-calls/calls/${id}/resolve`, { notes }, {
      requiresAuth: true,
    })
  }

  async triggerProviderCallSync(startDate: string, endDate: string, provider = 'twilio'): Promise<{ message: string }> {
    return this.post<{ message: string }>('/admin/provider-calls/sync', { startDate, endDate, provider }, {
      requiresAuth: true,
    })
  }

  async getProfitLoss(period: ProfitLossPeriod = '30d'): Promise<ProfitLossStats> {
    return this.get<ProfitLossStats>(`/admin/profit-loss?period=${period}`, {
      requiresAuth: true,
    })
  }

  async getProfitLossTrend(period: ProfitLossPeriod = '30d'): Promise<ProfitLossTrendResponse> {
    return this.get<ProfitLossTrendResponse>(`/admin/profit-loss-trend?period=${period}`, {
      requiresAuth: true,
    })
  }

  async getReferralProgrammes(page = 1, pageSize = 20): Promise<AdminReferralProgrammesResponse> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
    return this.get<AdminReferralProgrammesResponse>(`/admin/affiliates/programmes?${params.toString()}`, {
      requiresAuth: true,
    })
  }

  async createReferralProgramme(data: { rewards: ReferralReward[]; name?: string }): Promise<AdminReferralProgramme> {
    return this.post<AdminReferralProgramme>('/admin/affiliates/programmes', data, { requiresAuth: true })
  }

  async getActiveAffiliates(page = 1, pageSize = 20): Promise<AdminActiveAffiliatesResponse> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
    return this.get<AdminActiveAffiliatesResponse>(`/admin/affiliates/active?${params.toString()}`, { requiresAuth: true })
  }

  async createAffiliate(id: string, data: { code: string; sharePercentage: number; revenueShareMonths: number; rewards: ReferralReward[] }): Promise<AdminAffiliate> {
    return this.post<AdminAffiliate>(`/admin/affiliates/${id}`, data, { requiresAuth: true })
  }

  async updateAffiliate(id: string, data: { code: string; sharePercentage: number; revenueShareMonths: number; rewards: ReferralReward[] }): Promise<AdminAffiliate> {
    return this.patch<AdminAffiliate>(`/admin/affiliates/${id}`, data, { requiresAuth: true })
  }

  async getCampaigns(page = 1, pageSize = 50): Promise<AdminReferralCampaignsResponse> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
    return this.get<AdminReferralCampaignsResponse>(`/admin/affiliates/campaigns?${params.toString()}`, { requiresAuth: true })
  }

  async createCampaign(data: { code: string; name: string; description?: string; rewards: ReferralReward[]; maxUses?: number | null; activeFrom: string; activeUntil?: string | null }): Promise<AdminReferralCampaign> {
    return this.post<AdminReferralCampaign>('/admin/affiliates/campaigns', data, { requiresAuth: true })
  }

  async updateCampaign(id: string, data: { code: string; name: string; description?: string; rewards: ReferralReward[]; maxUses?: number | null; activeFrom: string; activeUntil?: string | null }): Promise<AdminReferralCampaign> {
    return this.patch<AdminReferralCampaign>(`/admin/affiliates/campaigns/${id}`, data, { requiresAuth: true })
  }

  async deleteCampaign(id: string): Promise<AdminReferralCampaign> {
    return this.delete<AdminReferralCampaign>(`/admin/affiliates/campaigns/${id}`, { requiresAuth: true })
  }

  async getInboxEmails(page = 1, pageSize = 20): Promise<AdminEmailsResponse> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
    return this.get<AdminEmailsResponse>(`/admin/inbox/emails?${params.toString()}`, {
      requiresAuth: true,
    })
  }

  async getInboxUnreadCount(): Promise<{ unreadCount: number }> {
    return this.get<{ unreadCount: number }>('/admin/inbox/unread-count', {
      requiresAuth: true,
    })
  }

  async getInboxEmail(uid: string): Promise<{ email: AdminEmailDetail }> {
    return this.get<{ email: AdminEmailDetail }>(`/admin/inbox/emails/${uid}`, {
      requiresAuth: true,
    })
  }

  // Scheduler API methods
  async getSchedulerTasks(): Promise<SchedulerTasksResponse> {
    return this.get<SchedulerTasksResponse>('/admin/scheduler/tasks', {
      requiresAuth: true,
    })
  }

  async getSchedulerTaskRuns(key: string): Promise<SchedulerRunsResponse> {
    return this.get<SchedulerRunsResponse>(`/admin/scheduler/tasks/${key}/runs`, {
      requiresAuth: true,
    })
  }

  async triggerSchedulerTask(key: string): Promise<{ run: SchedulerTaskRun }> {
    return this.post<{ run: SchedulerTaskRun }>(`/admin/scheduler/tasks/${key}/trigger`, {}, {
      requiresAuth: true,
    })
  }

  async updateSchedulerTask(key: string, data: { enabled: boolean }): Promise<{ task: SchedulerTask }> {
    return this.patch<{ task: SchedulerTask }>(`/admin/scheduler/tasks/${key}`, data, {
      requiresAuth: true,
    })
  }

  // Agent Calls API methods
  async getAgentVoices(): Promise<AgentVoicesResponse> {
    return this.get<AgentVoicesResponse>('/agent/voices', {
      requiresAuth: false,
    })
  }

  async getAgentCalls(page = 1, pageSize = 20): Promise<AdminAgentCallsResponse> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
    return this.get<AdminAgentCallsResponse>(`/agent/calls?${params.toString()}`, {
      requiresAuth: true,
    })
  }

  async getAgentCall(id: string): Promise<AdminAgentCallDetail> {
    return this.get<AdminAgentCallDetail>(`/agent/calls/${id}`, {
      requiresAuth: true,
    })
  }

  async initiateAgentCall(data: { to: string; voice?: string; language?: string; initialSayText?: string }): Promise<{ agentCallId: string; callId: string; status: string; provider: string }> {
    return this.post<{ agentCallId: string; callId: string; status: string; provider: string }>('/agent/calls', data, {
      requiresAuth: true,
    })
  }

  async initiateBrowserCall(data: { voice?: string; language?: string; initialSayText?: string }): Promise<{ agentCallId: string; callId: string; status: string; provider: string; wsPath: string }> {
    return this.post<{ agentCallId: string; callId: string; status: string; provider: string; wsPath: string }>('/agent/calls/browser', data, {
      requiresAuth: true,
    })
  }

  async pollAgentCallEvents(id: string, afterSequence = 0, timeout = 25): Promise<{ events: AgentCallEvent[] }> {
    const params = new URLSearchParams({ afterSequence: String(afterSequence), timeout: String(timeout) })
    return this.get<{ events: AgentCallEvent[] }>(`/agent/calls/${id}/events?${params.toString()}`, {
      requiresAuth: true,
    })
  }

  async agentCallSay(id: string, text: string, voice?: string): Promise<{ status: string }> {
    return this.post<{ status: string }>(`/agent/calls/${id}/say`, { text, voice }, {
      requiresAuth: true,
    })
  }

  async agentCallDtmf(id: string, digits: string): Promise<{ status: string }> {
    return this.post<{ status: string }>(`/agent/calls/${id}/dtmf`, { digits }, {
      requiresAuth: true,
    })
  }

  async agentCallHangup(id: string): Promise<{ success: boolean }> {
    return this.post<{ success: boolean }>(`/agent/calls/${id}/hangup`, {}, {
      requiresAuth: true,
    })
  }

  async syncCall(id: number): Promise<{ call: AdminCall }> {
    return this.post<{ call: AdminCall }>(`/admin/calls/${id}/sync`, {}, {
      requiresAuth: true,
    })
  }

}

export const adminApiService = new AdminApiService()
export default adminApiService

import { queryOptions } from '@tanstack/react-query'

export const adminQueries = {
  all: ['admin'] as const,

  // Invalidation-only prefixes (no queryOptions — used with invalidateQueries)
  pricingList: () => [...adminQueries.all, 'pricing'] as const,
  pricingVersionsList: () => [...adminQueries.all, 'pricing', 'versions'] as const,
  pricingActivationsList: () => [...adminQueries.all, 'pricing', 'activations'] as const,
  accountsList: () => [...adminQueries.all, 'accounts'] as const,
  providerCallsList: () => [...adminQueries.all, 'provider-calls'] as const,
  affiliatesList: () => [...adminQueries.all, 'affiliates'] as const,
  referralProgrammesList: () => [...adminQueries.all, 'referral-programmes'] as const,
  campaignsList: () => [...adminQueries.all, 'campaigns'] as const,
  agentCallsList: () => [...adminQueries.all, 'agent-calls'] as const,
  inboxList: () => [...adminQueries.all, 'inbox'] as const,

  // Leaf queries
  stats: () => queryOptions({
    queryKey: [...adminQueries.all, 'stats'] as const,
    queryFn: () => adminApiService.getStats(),
    staleTime: 30 * 1000,
  }),

  pricing: () => queryOptions({
    queryKey: [...adminQueries.all, 'pricing'] as const,
    queryFn: () => adminApiService.getPricing(),
    staleTime: 60 * 1000,
  }),

  pricingConfig: () => queryOptions({
    queryKey: [...adminQueries.all, 'pricing', 'config'] as const,
    queryFn: () => adminApiService.getPricingConfig(),
    staleTime: 60 * 1000,
  }),

  providers: () => queryOptions({
    queryKey: [...adminQueries.all, 'pricing', 'providers'] as const,
    queryFn: () => adminApiService.getProviders(),
    staleTime: 30 * 1000,
  }),

  pricingGenerationStatus: () => queryOptions({
    queryKey: [...adminQueries.all, 'pricing', 'generation', 'status'] as const,
    queryFn: () => adminApiService.getGenerationStatus(),
    staleTime: 0,
  }),

  pricingVersions: (page: number, pageSize: number) => queryOptions({
    queryKey: [...adminQueries.pricingVersionsList(), page, pageSize] as const,
    queryFn: () => adminApiService.getPricingVersions(page, pageSize),
    staleTime: 60 * 1000,
  }),

  pricingVersionRates: (versionId: string | null, originNumber?: string) => queryOptions({
    queryKey: [...adminQueries.all, 'pricing', 'versions', versionId ?? '', 'rates', originNumber] as const,
    queryFn: () => adminApiService.getPricingVersionRates(versionId!, originNumber),
    enabled: !!versionId,
    staleTime: 5 * 60 * 1000,
  }),

  pricingActivations: (page: number, pageSize: number) => queryOptions({
    queryKey: [...adminQueries.pricingActivationsList(), page, pageSize] as const,
    queryFn: () => adminApiService.getPricingActivations(page, pageSize),
    staleTime: 60 * 1000,
  }),

  accounts: (page: number, pageSize: number) => queryOptions({
    queryKey: [...adminQueries.accountsList(), page, pageSize] as const,
    queryFn: () => adminApiService.getAccounts(page, pageSize),
    staleTime: 30 * 1000,
  }),

  account: (accountId: string) => queryOptions({
    queryKey: [...adminQueries.all, 'account', accountId] as const,
    queryFn: () => adminApiService.getAccount(accountId),
    enabled: !!accountId,
    staleTime: 30 * 1000,
  }),

  accountCalls: (accountId: string, page: number, pageSize: number) => queryOptions({
    queryKey: [...adminQueries.all, 'account', accountId, 'calls', page, pageSize] as const,
    queryFn: () => adminApiService.getAccountCalls(accountId, page, pageSize),
    enabled: !!accountId,
    staleTime: 30 * 1000,
  }),

  accountTransactions: (accountId: string, page: number, pageSize: number) => queryOptions({
    queryKey: [...adminQueries.all, 'account', accountId, 'transactions', page, pageSize] as const,
    queryFn: () => adminApiService.getAccountTransactions(accountId, page, pageSize),
    enabled: !!accountId,
    staleTime: 30 * 1000,
  }),

  calls: (page: number, pageSize: number) => queryOptions({
    queryKey: [...adminQueries.all, 'calls', page, pageSize] as const,
    queryFn: () => adminApiService.getCalls(page, pageSize),
    staleTime: 30 * 1000,
  }),

  transactions: (page: number, pageSize: number) => queryOptions({
    queryKey: [...adminQueries.all, 'transactions', page, pageSize] as const,
    queryFn: () => adminApiService.getTransactions(page, pageSize),
    staleTime: 30 * 1000,
  }),

  providerCalls: (page: number, pageSize: number, filters?: Record<string, string | undefined>) => queryOptions({
    queryKey: [...adminQueries.providerCallsList(), page, pageSize, filters] as const,
    queryFn: () => adminApiService.getProviderCalls(page, pageSize, filters),
    staleTime: 30 * 1000,
  }),

  providerCallSyncSummary: () => queryOptions({
    queryKey: [...adminQueries.all, 'provider-calls', 'summary'] as const,
    queryFn: () => adminApiService.getProviderCallSyncSummary(),
    staleTime: 30 * 1000,
  }),

  profitLoss: (period: ProfitLossPeriod) => queryOptions({
    queryKey: [...adminQueries.all, 'profit-loss', period] as const,
    queryFn: () => adminApiService.getProfitLoss(period),
    staleTime: 60 * 1000,
  }),

  profitLossTrend: (period: ProfitLossPeriod) => queryOptions({
    queryKey: [...adminQueries.all, 'profit-loss-trend', period] as const,
    queryFn: () => adminApiService.getProfitLossTrend(period),
    staleTime: 60 * 1000,
  }),

  referralProgrammes: (page: number, pageSize: number) => queryOptions({
    queryKey: [...adminQueries.referralProgrammesList(), page, pageSize] as const,
    queryFn: () => adminApiService.getReferralProgrammes(page, pageSize),
    staleTime: 60 * 1000,
  }),

  activeAffiliates: (page: number, pageSize: number) => queryOptions({
    queryKey: [...adminQueries.affiliatesList(), 'active', page, pageSize] as const,
    queryFn: () => adminApiService.getActiveAffiliates(page, pageSize),
    staleTime: 30 * 1000,
  }),

  campaigns: (page: number, pageSize: number) => queryOptions({
    queryKey: [...adminQueries.campaignsList(), page, pageSize] as const,
    queryFn: () => adminApiService.getCampaigns(page, pageSize),
    staleTime: 30 * 1000,
  }),

  phoneNumbers: () => queryOptions({
    queryKey: [...adminQueries.all, 'phone-numbers'] as const,
    queryFn: () => adminApiService.getPhoneNumbers(),
    staleTime: 5 * 60 * 1000,
  }),

  inboxEmails: (page: number, pageSize: number) => queryOptions({
    queryKey: [...adminQueries.inboxList(), page, pageSize] as const,
    queryFn: () => adminApiService.getInboxEmails(page, pageSize),
    staleTime: 30 * 1000,
  }),

  inboxEmail: (uid: string) => queryOptions({
    queryKey: [...adminQueries.all, 'inbox', 'email', uid] as const,
    queryFn: () => adminApiService.getInboxEmail(uid),
    enabled: !!uid,
    staleTime: 60 * 1000,
  }),

  inboxUnreadCount: () => queryOptions({
    queryKey: [...adminQueries.inboxList(), 'unread-count'] as const,
    queryFn: () => adminApiService.getInboxUnreadCount(),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  }),

  schedulerTasks: () => queryOptions({
    queryKey: [...adminQueries.all, 'scheduler', 'tasks'] as const,
    queryFn: () => adminApiService.getSchedulerTasks(),
    staleTime: 10 * 1000,
  }),

  schedulerRuns: (key: string) => queryOptions({
    queryKey: [...adminQueries.all, 'scheduler', 'runs', key] as const,
    queryFn: () => adminApiService.getSchedulerTaskRuns(key),
    enabled: !!key,
    staleTime: 5 * 1000,
  }),

  agentCalls: (page: number, pageSize: number) => queryOptions({
    queryKey: [...adminQueries.agentCallsList(), page, pageSize] as const,
    queryFn: () => adminApiService.getAgentCalls(page, pageSize),
    staleTime: 10 * 1000,
  }),

  agentCall: (id: string) => queryOptions({
    queryKey: [...adminQueries.all, 'agent-call', id] as const,
    queryFn: () => adminApiService.getAgentCall(id),
    enabled: !!id,
    staleTime: 5 * 1000,
  }),

  agentVoices: () => queryOptions({
    queryKey: [...adminQueries.all, 'agent-voices'] as const,
    queryFn: () => adminApiService.getAgentVoices(),
    staleTime: 5 * 60 * 1000,
  }),
}
